import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileCheck2, Loader2, RefreshCw, Printer, Send, Edit3, CheckCircle2, Sparkles, Trash2,
  Stethoscope, ClipboardList, Pill, ClipboardCheck, MessageSquare, CalendarDays,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { format, isValid } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { useEncounter } from '@/contexts/EncounterContext';
import { avsService, AfterVisitSummary, AvsAiField } from '@/services/avsService';
import { parseApiError } from '@/lib/apiErrors';
import { cacheKeys } from '@/lib/cacheKeys';

const AvsPreviewPanel: React.FC = () => {
  const { clinicId, encounterId, appointment } = useEncounter();
  const qc = useQueryClient();
  const [busy, setBusy] = useState<'generate' | 'save' | 'publish' | 'pdf' | 'discard' | null>(null);
  const [confirmRegenOpen, setConfirmRegenOpen] = useState(false);
  const [edits, setEdits] = useState<{
    visit_summary: string;
    patient_instructions: string;
    follow_up_date: string;
    follow_up_notes: string;
  }>({
    visit_summary: '', patient_instructions: '', follow_up_date: '', follow_up_notes: '',
  });

  const { data: avs, isLoading, refetch } = useQuery({
    queryKey: cacheKeys.encounterAvs(encounterId),
    queryFn: () => avsService.getAvs(clinicId, encounterId),
  });

  // Reset local edits whenever the server value of any rendered field
  // changes (post-generate, post-discard, post-save). Tracking the
  // fields directly is more reliable than tracking only `updatedAt`.
  // Note: timestamps come back camelCase from Sequelize (`updatedAt`)
  // even though all explicit columns are snake_case.
  useEffect(() => {
    if (avs) {
      setEdits({
        visit_summary: avs.visit_summary || '',
        patient_instructions: avs.patient_instructions || '',
        follow_up_date: avs.follow_up_date || '',
        follow_up_notes: avs.follow_up_notes || '',
      });
    }
  }, [
    avs?.id,
    avs?.updatedAt,
    avs?.visit_summary,
    avs?.patient_instructions,
    avs?.follow_up_date,
    avs?.follow_up_notes,
  ]);

  const hasUnsavedEdits = !!avs && (
    edits.visit_summary !== (avs.visit_summary || '')
    || edits.patient_instructions !== (avs.patient_instructions || '')
    || edits.follow_up_date !== (avs.follow_up_date || '')
    || edits.follow_up_notes !== (avs.follow_up_notes || '')
  );

  const runGenerate = async () => {
    setBusy('generate');
    try {
      await avsService.generateAvs(clinicId, encounterId);
      await qc.invalidateQueries({ queryKey: ['encounter', encounterId, 'avs'] });
      toast({ title: 'AVS draft generated', variant: 'success' });
    } catch (err) {
      const e = parseApiError(err);
      toast({ title: 'Generation failed', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const generate = () => {
    if (avs && hasUnsavedEdits) {
      setConfirmRegenOpen(true);
      return;
    }
    runGenerate();
  };

  const discardAi = async (field: AvsAiField) => {
    setBusy('discard');
    try {
      await avsService.discardAiField(clinicId, encounterId, field);
      await qc.invalidateQueries({ queryKey: ['encounter', encounterId, 'avs'] });
      toast({ title: 'AI content discarded', variant: 'success' });
    } catch (err) {
      const e = parseApiError(err);
      toast({ title: 'Could not discard', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    setBusy('save');
    try {
      await avsService.updateAvs(clinicId, encounterId, {
        ...edits,
        follow_up_date: edits.follow_up_date.trim() ? edits.follow_up_date : null,
      });
      await qc.invalidateQueries({ queryKey: ['encounter', encounterId, 'avs'] });
      toast({ title: 'AVS saved', variant: 'success' });
    } catch (err) {
      const e = parseApiError(err);
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const publish = async (sendEmail: boolean) => {
    setBusy('publish');
    try {
      // Persist any in-flight edits before flipping status — the publish
      // endpoint only changes status fields, so unsaved textarea/input
      // values would otherwise be dropped on the floor.
      if (hasUnsavedEdits) {
        await avsService.updateAvs(clinicId, encounterId, {
          ...edits,
          follow_up_date: edits.follow_up_date.trim() ? edits.follow_up_date : null,
        });
      }
      const result = await avsService.publishAvs(clinicId, encounterId, { sendEmail });
      await qc.invalidateQueries({ queryKey: ['encounter', encounterId, 'avs'] });
      const emailDelivered = sendEmail && result?.status === 'sent';
      toast({
        title: emailDelivered
          ? 'AVS published & emailed'
          : sendEmail
            ? 'AVS published — email could not be sent (no email on file)'
            : 'AVS published',
        variant: 'success',
      });
    } catch (err) {
      const e = parseApiError(err);
      toast({ title: 'Publish failed', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const downloadPdf = async () => {
    setBusy('pdf');
    try {
      const blob = await avsService.downloadAvsPdf(clinicId, encounterId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `avs-${encounterId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({
        title: 'PDF downloaded',
        description: 'The After-Visit Summary PDF has been saved to your device.',
        variant: 'success',
      });
    } catch (err) {
      const e = parseApiError(err);
      toast({ title: 'PDF download failed', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  if (isLoading) {
    return (
      <div className='flex justify-center py-8'>
        <Loader2 className='h-6 w-6 animate-spin text-slate-400' />
      </div>
    );
  }

  if (!avs) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='text-base flex items-center gap-2'>
            <FileCheck2 className='h-4 w-4' /> After-Visit Summary
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <p className='text-sm text-slate-500'>
            No AVS yet. Generate a draft from the encounter content (chief complaint,
            diagnoses, medication changes, orders placed) and edit before publishing.
          </p>
          <Button onClick={runGenerate} disabled={busy === 'generate'}>
            {busy === 'generate' ? (
              <><Loader2 className='mr-2 h-4 w-4 animate-spin' /> Generating…</>
            ) : (
              <><RefreshCw className='mr-2 h-4 w-4' /> Generate AVS draft</>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <AvsLoaded
        avs={avs}
        edits={edits}
        setEdits={setEdits}
        busy={busy}
        onGenerate={generate}
        onSave={save}
        onPublish={publish}
        onPdf={downloadPdf}
        onRefetch={refetch}
        onDiscardAi={discardAi}
      />
      <AlertDialog open={confirmRegenOpen} onOpenChange={setConfirmRegenOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate AVS?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved edits. Regenerating will fill any empty fields with fresh
              AI content; fields you've already edited will be preserved. To replace an
              edited field with a new AI draft, discard it first using the trash button.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmRegenOpen(false); runGenerate(); }}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const AvsLoaded: React.FC<{
  avs: AfterVisitSummary;
  edits: any;
  setEdits: (e: any) => void;
  busy: 'generate' | 'save' | 'publish' | 'pdf' | 'discard' | null;
  onGenerate: () => void;
  onSave: () => void;
  onPublish: (email: boolean) => void;
  onPdf: () => void;
  onRefetch: () => void;
  onDiscardAi: (field: AvsAiField) => void;
}> = ({ avs, edits, setEdits, busy, onGenerate, onSave, onPublish, onPdf, onDiscardAi }) => {
  const isPublished = avs.status === 'published' || avs.status === 'sent' || avs.status === 'acknowledged';
  const aiOn = (field: AvsAiField) => !!avs.ai_provenance?.[field];

  const todayDiagnoses = (avs.diagnoses || []).filter(
    d => !d.category || d.category === 'encounter-diagnosis',
  );
  const activeProblems = (avs.diagnoses || []).filter(
    d => d.category === 'problem-list-item',
  );

  // Parse the stored YYYY-MM-DD string into a Date for the DatePicker, and
  // serialize back on change. Empty string clears the field.
  const followUpDateValue = edits.follow_up_date
    ? (() => {
      const parsed = new Date(`${edits.follow_up_date}T00:00:00`);
      return isValid(parsed) ? parsed : undefined;
    })()
    : undefined;

  const setFollowUpDate = (d: Date | undefined) => {
    setEdits({
      ...edits,
      follow_up_date: d && isValid(d) ? format(d, 'yyyy-MM-dd') : '',
    });
  };

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader className='flex-row items-center justify-between space-y-0 border-b border-slate-100 pb-4'>
          <CardTitle className='text-base flex items-center gap-2'>
            <FileCheck2 className='h-4 w-4 text-brand-teal' /> After-Visit Summary
            <Badge
              variant='outline'
              className={isPublished
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-amber-200 bg-amber-50 text-amber-700'
              }
            >
              {avs.status}
            </Badge>
          </CardTitle>
          <div className='flex gap-2'>
            <Button variant='outline' size='sm' onClick={onGenerate} disabled={!!busy}>
              <RefreshCw className='mr-1 h-3 w-3' /> Regenerate
            </Button>
            <Button variant='outline' size='sm' onClick={onPdf} disabled={!!busy}>
              {busy === 'pdf'
                ? <Loader2 className='mr-1 h-3 w-3 animate-spin' />
                : <Printer className='mr-1 h-3 w-3' />}
              PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent className='space-y-5 pt-5'>
          {/* Clinical summary — read-only, derived from the encounter. Grouped
              under a single heading so the long list of diagnoses, problems,
              meds and orders doesn't fight with the editable fields below. */}
          {(todayDiagnoses.length > 0
            || activeProblems.length > 0
            || (avs.medication_changes?.length || 0) > 0
            || (avs.orders?.length || 0) > 0) && (
            <div className='space-y-4 rounded-md border border-slate-100 bg-slate-50/60 p-4'>
              {todayDiagnoses.length > 0 && (
                <Section title="Today's diagnoses" icon={Stethoscope}>
                  <ul className='list-disc space-y-1 pl-5 text-sm text-slate-700'>
                    {todayDiagnoses.map((d, i) => (
                      <li key={i}>{d.display}{d.code ? ` (${d.code})` : ''}</li>
                    ))}
                  </ul>
                </Section>
              )}

              {activeProblems.length > 0 && (
                <Section title='Active problem list' icon={ClipboardList}>
                  <ul className='list-disc space-y-1 pl-5 text-sm text-slate-700'>
                    {activeProblems.map((d, i) => (
                      <li key={i}>{d.display}{d.code ? ` (${d.code})` : ''}</li>
                    ))}
                  </ul>
                </Section>
              )}

              {avs.medication_changes && avs.medication_changes.length > 0 && (
                <Section title='Medication changes' icon={Pill}>
                  <ul className='space-y-1 text-sm text-slate-700'>
                    {avs.medication_changes.map((m, i) => (
                      <li key={i} className='flex items-start gap-2'>
                        <Badge variant='outline' className='shrink-0 capitalize'>{m.action}</Badge>
                        <span>
                          <span className='font-medium'>{m.medication_display}</span>
                          {m.dose_text ? ` — ${m.dose_text}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {avs.orders && avs.orders.length > 0 && (
                <Section title='Orders placed' icon={ClipboardCheck}>
                  <ul className='space-y-1 text-sm text-slate-700'>
                    {avs.orders.map((o, i) => (
                      <li key={i}>
                        <span className='font-medium capitalize'>{o.category}</span>: {o.display}
                        {o.patient_instructions && (
                          <div className='ml-1 text-xs text-slate-500'>{o.patient_instructions}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </div>
          )}

          {avs.diagnosis_explanations && avs.diagnosis_explanations.length > 0 && (
            <Section title='Plain-language diagnosis explanations'>
              {aiOn('diagnosis_explanations') && (
                <div className='mb-2 flex items-center gap-2'>
                  <AiBadge />
                  {!isPublished && (
                    <DiscardAiButton
                      busy={busy === 'discard'}
                      onClick={() => onDiscardAi('diagnosis_explanations')}
                    />
                  )}
                </div>
              )}
              <ul className='space-y-2 text-sm text-slate-700'>
                {avs.diagnosis_explanations.map((d, i) => (
                  <li key={i} className='rounded-md border border-slate-200 p-2'>
                    <div className='font-medium'>{d.display}{d.code ? ` (${d.code})` : ''}</div>
                    <div className='mt-1 text-slate-600'>{d.plain_language}</div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <div className='space-y-2'>
            <div className='flex items-center justify-between'>
              <Label htmlFor='avs-summary' className='flex items-center gap-1.5 text-sm font-medium text-slate-700'>
                <MessageSquare className='h-3.5 w-3.5 text-slate-400' />
                Visit summary (patient-facing)
              </Label>
              {aiOn('visit_summary') && (
                <div className='flex items-center gap-2'>
                  <AiBadge />
                  {!isPublished && (
                    <DiscardAiButton
                      busy={busy === 'discard'}
                      onClick={() => onDiscardAi('visit_summary')}
                    />
                  )}
                </div>
              )}
            </div>
            <Textarea
              id='avs-summary'
              className='min-h-[80px]'
              value={edits.visit_summary}
              onChange={e => setEdits({ ...edits, visit_summary: e.target.value })}
              disabled={isPublished}
            />
          </div>

          <div className='space-y-2'>
            <div className='flex items-center justify-between'>
              <Label htmlFor='avs-instructions' className='flex items-center gap-1.5 text-sm font-medium text-slate-700'>
                <Edit3 className='h-3.5 w-3.5 text-slate-400' />
                Patient instructions
              </Label>
              {aiOn('patient_instructions') && (
                <div className='flex items-center gap-2'>
                  <AiBadge />
                  {!isPublished && (
                    <DiscardAiButton
                      busy={busy === 'discard'}
                      onClick={() => onDiscardAi('patient_instructions')}
                    />
                  )}
                </div>
              )}
            </div>
            <Textarea
              id='avs-instructions'
              className='min-h-[100px]'
              placeholder='Take medications as prescribed. Follow up in 4 weeks…'
              value={edits.patient_instructions}
              onChange={e => setEdits({ ...edits, patient_instructions: e.target.value })}
              disabled={isPublished}
            />
          </div>

          <div className='grid gap-3 sm:grid-cols-[200px_1fr]'>
            <div className='space-y-1.5'>
              <Label htmlFor='avs-follow-up' className='flex items-center gap-1.5 text-xs font-medium text-slate-600'>
                <CalendarDays className='h-3.5 w-3.5 text-slate-400' />
                Follow-up date
              </Label>
              <DatePicker
                value={followUpDateValue}
                onValueChange={setFollowUpDate}
                disabled={isPublished}
                placeholder='MM/DD/YYYY'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='avs-follow-up-notes' className='text-xs font-medium text-slate-600'>
                Follow-up notes
              </Label>
              <Input
                id='avs-follow-up-notes'
                value={edits.follow_up_notes}
                onChange={e => setEdits({ ...edits, follow_up_notes: e.target.value })}
                disabled={isPublished}
              />
            </div>
          </div>

          {!isPublished && (
            <div className='flex gap-2 pt-2'>
              <Button variant='outline' onClick={onSave} disabled={!!busy}>
                {busy === 'save'
                  ? <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  : <Edit3 className='mr-2 h-4 w-4' />}
                Save draft
              </Button>
              <Button onClick={() => onPublish(true)} disabled={!!busy} className='bg-brand-teal hover:bg-brand-teal/90'>
                {busy === 'publish'
                  ? <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  : <Send className='mr-2 h-4 w-4' />}
                Publish &amp; email patient
              </Button>
              <Button variant='ghost' onClick={() => onPublish(false)} disabled={!!busy}>
                Publish only
              </Button>
            </div>
          )}

          {isPublished && (
            <div className='flex items-center gap-2 rounded-md bg-emerald-50 p-3 text-sm text-emerald-800'>
              <CheckCircle2 className='h-4 w-4' />
              Published {avs.published_at ? `on ${new Date(avs.published_at).toLocaleString('en-US')}` : ''}.
              {avs.acknowledged_at && ' Patient has acknowledged.'}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const Section: React.FC<{
  title: string;
  children: React.ReactNode;
  icon?: React.ElementType;
}> = ({ title, children, icon: Icon }) => (
  <div>
    <h4 className='mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500'>
      {Icon && <Icon className='h-3.5 w-3.5 text-slate-400' />}
      {title}
    </h4>
    {children}
  </div>
);

const AiBadge: React.FC = () => (
  <Badge
    variant='outline'
    className='border-violet-200 bg-violet-50 text-violet-700'
    title='Generated by AI — please review before publishing.'
  >
    <Sparkles className='mr-1 h-3 w-3' /> AI · review
  </Badge>
);

const DiscardAiButton: React.FC<{ busy: boolean; onClick: () => void }> = ({ busy, onClick }) => (
  <Button
    variant='ghost'
    size='sm'
    className='h-6 px-2 text-xs text-slate-500 hover:text-rose-600'
    onClick={onClick}
    disabled={busy}
    title='Discard AI-generated content for this field'
  >
    {busy ? <Loader2 className='h-3 w-3 animate-spin' /> : <Trash2 className='h-3 w-3' />}
  </Button>
);

export default AvsPreviewPanel;
